import FieldLabelMobile from '@/domains/mobile-web/components/FieldLabelMobile/FieldLabelMobile';
import { cn } from '@/lib/utils';

export interface RadioOptionMobile<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupMobileProps<T extends string = string> {
  label?: string;
  /** Shared by every radio in the group */
  name: string;
  options: RadioOptionMobile<T>[];
  /** Controlled value; leave out (with `defaultValue`) for an uncontrolled group */
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  /** class untuk pembungkus (label + options) */
  className?: string;
  /** 'row' puts the options side by side (default), 'column' stacks them */
  direction?: 'row' | 'column';
}

const RadioGroupMobile = <T extends string = string>({
  label,
  name,
  options,
  value,
  defaultValue,
  onChange,
  required,
  disabled,
  error,
  helperText,
  className,
  direction = 'row',
}: RadioGroupMobileProps<T>) => (
  <FieldLabelMobile
    label={label}
    required={required}
    error={error}
    helperText={helperText}
    className={className}
  >
    <div
      role="radiogroup"
      aria-label={label}
      aria-invalid={!!error}
      className={cn(
        'flex gap-6',
        direction === 'column' ? 'flex-col gap-3' : 'flex-wrap items-center'
      )}
    >
      {options.map((option) => {
        const isDisabled = disabled || option.disabled;
        return (
          <label
            key={option.value}
            className={cn(
              'inline-flex items-center gap-2',
              isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === undefined ? undefined : value === option.value}
              defaultChecked={value === undefined ? defaultValue === option.value : undefined}
              onChange={onChange ? () => onChange(option.value) : undefined}
              disabled={isDisabled}
              className="h-5 w-5 shrink-0 cursor-pointer accent-text-blue-100 disabled:cursor-not-allowed"
            />
            <span className="body1 text-black">{option.label}</span>
          </label>
        );
      })}
    </div>
  </FieldLabelMobile>
);

export default RadioGroupMobile;
